import { arrayMove } from '@dnd-kit/sortable'
import isEqual from 'lodash-es/isEqual'

import { EMPTY_LIST, EMPTY_STRING } from '../../../constants/common'
import { IMAGE_TYPES } from '../../../constants/file'

import {
  PHOTO_ACCEPTED_TYPES,
  PHOTO_ERROR_TEXT_KEY,
  PHOTO_FALLBACK_MIME_TYPE,
  PHOTO_MAX_SIZE,
  PHOTO_REJECTION_TOO_LARGE,
} from './RentalPortalPhotoGallery.constants'

function getPersistedPhotosOrder(photos) {
  return getPhotosOrderPayload(photos).filter((item, index) =>
    getIsPhotoPersisted(photos[index])
  )
}

function getIsPhotoAcceptedType(mimeType) {
  return Object.keys(PHOTO_ACCEPTED_TYPES).includes(mimeType)
}

function validatePhotoFile(file) {
  return file.size > PHOTO_MAX_SIZE
    ? {
        code: PHOTO_REJECTION_TOO_LARGE,
        message: PHOTO_ERROR_TEXT_KEY.rejectedSize,
      }
    : null
}

function getPhotoUrlName(photo) {
  return (photo.url ?? photo.thumb_url ?? EMPTY_STRING).split('/').pop()
}

function getPhotoMimeType(photo) {
  const extension = getPhotoUrlName(photo).split('.').pop().toLowerCase()

  return (
    photo.mime_type ??
    IMAGE_TYPES.find(type => type.endsWith(`/${extension}`)) ??
    PHOTO_FALLBACK_MIME_TYPE
  )
}

export function getIsPhotoPersisted(photo) {
  return Boolean(photo.file_id)
}

export function getPhotoFileId(photo) {
  return photo.file_id ?? photo.id
}

export function getPhotoName(photo) {
  return photo.filename || photo.name || null
}

export function getPhotoDisplayName(photo, t) {
  return getPhotoName(photo) ?? t('RentalPortal.ListingPhotoFallbackNameKey')
}

export function getPhotoById({ photoId, photos }) {
  return photos.find(photo => photo.id === photoId) ?? null
}

export function getPhotoCoverIndex(photos) {
  if (photos.length === 0) {
    return -1
  }

  return Math.max(
    photos.findIndex(photo => photo.is_cover),
    0
  )
}

export function getPhotoDeleteConfirmationKey({ photo, photos }) {
  return getPhotoCoverIndex(photos) ===
    photos.findIndex(item => item.id === photo.id)
    ? 'RentalPortal.ListingPhotoDeleteCoverConfirmationTextKey'
    : 'RentalPortal.ListingPhotoDeleteConfirmationTextKey'
}

export function getPhotosWithCover(photos, photoId) {
  return photos.map(photo => ({ ...photo, is_cover: photo.id === photoId }))
}

export function getReorderedPhotos(photos, activeId, overId) {
  const oldIndex = photos.findIndex(photo => photo.id === activeId)
  const newIndex = photos.findIndex(photo => photo.id === overId)

  return oldIndex < 0 || newIndex < 0
    ? photos
    : arrayMove(photos, oldIndex, newIndex)
}

export function getPhotosOrderPayload(photos) {
  const coverIndex = getPhotoCoverIndex(photos)

  return photos.map((photo, index) => ({
    file_id: getPhotoFileId(photo),
    is_cover: index === coverIndex,
    position: index + 1,
  }))
}

export function getPhotosAttachPayload(photos) {
  return getPhotosOrderPayload(photos).filter(
    (item, index) => !getIsPhotoPersisted(photos[index])
  )
}

export function getUnpersistedPhotos(photos) {
  return photos.filter(photo => !getIsPhotoPersisted(photo))
}

export function getPhotoMoves({ currentFileIds, targetFileIds }) {
  return targetFileIds.reduce(
    ({ moves, order }, fileId, index) =>
      order[index] === fileId
        ? { moves, order }
        : {
            moves: [...moves, { fileId, position: index + 1 }],
            order: [
              ...order.slice(0, index),
              fileId,
              ...order
                .slice(index)
                .filter(orderFileId => orderFileId !== fileId),
            ],
          },
    { moves: EMPTY_LIST, order: currentFileIds }
  ).moves
}

export function getPhotoCoverToSave({ photos, savedPhotos }) {
  const cover = photos[getPhotoCoverIndex(photos)]

  return cover &&
    getIsPhotoPersisted(cover) &&
    savedPhotos.findIndex(
      photo => getPhotoFileId(photo) === getPhotoFileId(cover)
    ) !== getPhotoCoverIndex(savedPhotos)
    ? cover
    : null
}

export function getPhotosMarkedPersisted(photos, attachedFileIds) {
  return photos.map(photo =>
    attachedFileIds.includes(getPhotoFileId(photo))
      ? { ...photo, file_id: getPhotoFileId(photo) }
      : photo
  )
}

export function getHasChangedPhotoOrder({ initialPhotos, photos }) {
  return !isEqual(
    getPersistedPhotosOrder(initialPhotos),
    getPersistedPhotosOrder(photos)
  )
}

export function getHasChangedPhotos({ initialPhotos, photos }) {
  return !isEqual(
    getPhotosOrderPayload(initialPhotos ?? EMPTY_LIST),
    getPhotosOrderPayload(photos ?? EMPTY_LIST)
  )
}

export function toPhotoFormValues(photos) {
  return [...(photos ?? EMPTY_LIST)]
    .sort((a, b) => a.position - b.position)
    .map(photo => ({
      ...photo,
      filename: getPhotoName(photo) ?? getPhotoUrlName(photo),
      id: getPhotoFileId(photo),
      mime_type: getPhotoMimeType(photo),
    }))
}

export function getPhotoImageSrc(photo) {
  return photo.thumb_url || photo.url || photo.thumbnail || photo.origin || null
}

export function toPhotoPreviewFiles(photos) {
  return photos.map(photo =>
    photo.url ? { ...photo, localUrl: photo.url, origin: photo.url } : photo
  )
}

export function getPhotoDropzoneOptions() {
  return {
    accept: PHOTO_ACCEPTED_TYPES,
    multiple: true,
    validator: validatePhotoFile,
  }
}

export function getIsPhotoRetriable(file) {
  return Array.isArray(file.origin)
}

export function getPhotoErrorTextKey(file) {
  if (getIsPhotoRetriable(file)) {
    return PHOTO_ERROR_TEXT_KEY.uploadFailed
  }

  return getIsPhotoAcceptedType(file.mime_type)
    ? PHOTO_ERROR_TEXT_KEY.rejectedSize
    : PHOTO_ERROR_TEXT_KEY.rejectedType
}

export function getPhotoReorderState({
  disabled,
  disabledReason,
  hasUploading,
  photosCount,
  t,
}) {
  const hasReorder = photosCount > 1

  return {
    hasReorder,
    isReorderEnabled: hasReorder && !disabled && !hasUploading,
    reorderDisabledReason: disabled
      ? disabledReason
      : hasUploading
        ? t('RentalPortal.ListingPhotoReorderUploadingKey')
        : undefined,
  }
}

export function getPhotoCoverTooltip({ disabled, disabledReason, isCover, t }) {
  if (isCover) {
    return t('RentalPortal.ListingPhotoIsCoverKey')
  }

  return disabled ? disabledReason : t('RentalPortal.ListingPhotoSetCoverKey')
}

export function getPhotoRetryTooltip({ disabled, disabledReason, t }) {
  return disabled ? disabledReason : t('RentalPortal.ListingPhotoRetryKey')
}

export function getPhotoDeleteTooltip({ disabled, disabledReason, t }) {
  return disabled ? disabledReason : t('RentalPortal.ListingPhotoDeleteKey')
}

export function toPhotoRetryFile(photo) {
  return { ...photo, error: null, uploading: true }
}

export function getPhotoTileId(name, index) {
  return `${name}#${index}`
}

export function getPhotoFileInstance(file) {
  return Array.isArray(file.origin) ? (file.origin[0] ?? null) : null
}

export function toPhotoUploadFailures(files) {
  return files.map(file => ({
    ...file,
    error: { isPersistent: true, text: PHOTO_ERROR_TEXT_KEY.uploadFailed },
  }))
}

export function toPhotoUploadTiles({ files, startIndex, tileIdByFile }) {
  return files.map((file, index) => {
    const tileId = getPhotoTileId(file.name, startIndex + index)

    const instance = getPhotoFileInstance(file)

    if (instance) {
      tileIdByFile.set(instance, tileId)
    }

    return { ...file, id: tileId }
  })
}

export function getPhotoTileIdByFile({ file, tileIdByFile }) {
  const instance = getPhotoFileInstance(file)

  return instance ? (tileIdByFile.get(instance) ?? null) : null
}

export function getPhotoUploadResultTileIds({ files, results, tileIdByFile }) {
  return results
    .map((result, index) => [
      result,
      getPhotoTileIdByFile({ file: files[index], tileIdByFile }),
    ])
    .filter(([result, tileId]) => result && tileId)
}

export function getPhotoUploadResult(files) {
  return {
    acceptedFiles: files.filter(file => file?.id && !file.error),
    rejectedFiles: files.filter(file => !file?.id || file.error),
  }
}
